import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Play } from 'lucide-react';
import { settingsApi } from '@/services/api';

// Shipped slides (1920x700), served by app.js from the /slides folder.
// Used whenever the admin has not set a custom slide list.
const defaultSlides = [
  '/slides/slid1.jpg',
  '/slides/slid2.jpg',
  '/slides/slid3.jpg',
  '/slides/slid4.jpg',
];

// Slider height comes from the admin settings: keep the slides' own aspect
// ratio (default), fill the viewport, or use a fixed pixel height.
const sliderSizeStyle = (s) => {
  const min = Number(s?.hero_slider_min_height_px) || 480;
  const mode = s?.hero_slider_size_mode || 'ratio';

  if (mode === 'screen') return { height: '100vh' };
  if (mode === 'fixed') {
    return { height: `${Number(s?.hero_slider_height_px) || 700}px`, minHeight: `${min}px` };
  }
  const w = Number(s?.hero_slider_ratio_w) || 1920;
  const h = Number(s?.hero_slider_ratio_h) || 700;
  return { aspectRatio: `${w} / ${h}`, minHeight: `${min}px` };
};

// Renders an internal route via <Link>, or an external/absolute URL via <a>.
const HeroCta = ({ to, children, ...props }) => {
  const isExternal = /^(https?:)?\/\/|^(mailto:|tel:|wa\.me)/i.test(to || '');
  if (isExternal) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to || '/'} {...props}>
      {children}
    </Link>
  );
};

const HeroSection = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hero, setHero] = useState(null);

  useEffect(() => {
    settingsApi.get().then((res) => setHero(res.data)).catch(() => {});
  }, []);

  const slides = (hero?.hero_slides || []).filter(Boolean);
  const heroSlides = slides.length ? slides : defaultSlides;

  useEffect(() => {
    setCurrentSlide(0);
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const pick = (en, ar) => (isRTL ? (ar || en) : (en || ar)) || '';
  const title = (hero && pick(hero.hero_title_en, hero.hero_title_ar)) || t('hero.title');
  const subtitle = (hero && pick(hero.hero_subtitle_en, hero.hero_subtitle_ar)) || t('hero.subtitle');
  const primaryText =
    (hero && pick(hero.hero_cta_primary_text_en, hero.hero_cta_primary_text_ar)) || t('hero.cta');
  const secondaryText =
    (hero && pick(hero.hero_cta_secondary_text_en, hero.hero_cta_secondary_text_ar)) ||
    t('hero.ctaSecondary');
  const primaryLink = (hero && hero.hero_cta_primary_link) || '/projects';
  const secondaryLink = (hero && hero.hero_cta_secondary_link) || '/contact';

  return (
    <section
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-charcoal"
      style={sliderSizeStyle(hero)}
    >
      {/* Background Images */}
      {heroSlides.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div
            className={`absolute inset-0 bg-center bg-no-repeat ${
              hero?.hero_slider_fit === 'contain' ? 'bg-contain' : 'bg-cover'
            }`}
            style={{ backgroundImage: `url(${image})` }}
          />
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>
      ))}

      {/* Content */}
      <div className="relative h-full flex items-center z-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-3xl"
          >
            <motion.h1
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl lg:text-7xl text-white font-bold leading-tight mb-6"
            >
              {title}
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg md:text-xl text-white/80 mb-10 max-w-xl"
            >
              {subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap gap-4"
            >
              <HeroCta
                to={primaryLink}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-gold text-charcoal px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-gold/90 transition-colors duration-300"
              >
                {primaryText}
                <Arrow size={18} className="group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
              </HeroCta>

              <HeroCta
                to={secondaryLink}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-white/30 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-cream hover:text-charcoal transition-colors duration-300"
              >
                {secondaryText}
              </HeroCta>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            data-testid={`hero-slide-${index}`}
            className={`w-12 h-1 transition-all duration-300 ${
              index === currentSlide ? 'bg-gold' : 'bg-white/30'
            }`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 end-8 hidden md:flex flex-col items-center gap-2 text-white/60"
      >
        <span className="text-xs uppercase tracking-widest rotate-90 origin-center mb-8">Scroll</span>
        <motion.div 
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-px h-16 bg-gradient-to-b from-white/60 to-transparent"
        />
      </motion.div>
    </section>
  );
};

export default HeroSection;
